import { mutate } from "swr";
import { apiCall, useApi } from "../../api/client";
import { apiKey } from "../../api/types";
import { help } from "../../api/contracts/help";
import { ReferenceListEditor } from "../settings/reference-list-editor";

/**
 * /help/settings CRUD for help categories. The default seed writes
 * six shell-owned categories (getting-started, shell, tasks-and-
 * projects, agents-and-goals, for-developers, changelog) plus one
 * per installed product. Users can add their own categories here.
 *
 * Archiving a category hides it from /help but does not delete the
 * articles underneath (those keep their category_id; the join
 * filter in listCategories excludes archived rows).
 *
 * The slug/name CRUD list, add row, inline-rename, remove, empty
 * state, and error surface are owned by the canonical
 * `ReferenceListEditor` (a category IS a reference row: id + slug +
 * name). The optional `description`/`icon`/`sort_key` columns aren't
 * part of ReferenceListEditor's contract, so create sends a null
 * description; descriptions are managed elsewhere.
 */
export function HelpCategoryEditor() {
  const { data, isLoading } = useApi(help.categories.list, { query: {} });
  const categories = data?.categories ?? [];

  const refresh = () => mutate(apiKey(help.categories.list, { query: {} }));

  if (isLoading) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }

  return (
    <ReferenceListEditor
      title="Categories"
      description="Categories group help articles. The default seed writes six; products contribute one each."
      itemNoun="category"
      rows={categories.map((c) => ({ id: c.id, slug: c.slug, name: c.name }))}
      onCreate={async ({ slug, name }) => {
        await apiCall(help.categories.create, {
          body: {
            slug:
              slug.trim() ||
              name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-"),
            name: name.trim(),
            description: null,
          },
        });
        await refresh();
      }}
      onUpdate={async (id, patch) => {
        if (patch.name === undefined) return;
        await apiCall(help.categories.update, {
          params: { id },
          body: { name: patch.name },
        });
        await refresh();
      }}
      onArchive={async (id) => {
        await apiCall(help.categories.archive, { params: { id } });
        await refresh();
      }}
    />
  );
}
